/**
 * Mirrors the backend DTOs exactly. Nullability here is not defensive typing —
 * every `| null` below is a field the API genuinely returns as null, and the design
 * (canvas 1a, "Stat block") requires those to render as "—", never as 0.
 *
 * Backend sources:
 *   backend/src/modules/<module>/infrastructure/http/dto/
 *   backend/src/shared/domain/types/geojson.types.ts
 */

// ---------------------------------------------------------------- GeoJSON
// Copied verbatim from backend/src/shared/domain/types/geojson.types.ts.
// Coordinate order is [longitude, latitude] throughout (Mongo 2dsphere convention).

export type GeoJSONPoint = { type: 'Point'; coordinates: [number, number] };
export type GeoJSONLineString = { type: 'LineString'; coordinates: [number, number][] };
export type GeoJSONPolygon = { type: 'Polygon'; coordinates: [number, number][][] };
export type GeoJSONGeometry = GeoJSONPoint | GeoJSONLineString | GeoJSONPolygon;

export type HoleFeatureKind =
  | 'green'
  | 'fairway'
  | 'rough'
  | 'bunker'
  | 'water'
  | 'ob'
  | 'tee'
  | 'tree';

export type GeoJSONFeature = {
  type: 'Feature';
  geometry: GeoJSONGeometry;
  properties: { kind: HoleFeatureKind; tee?: string; [extra: string]: unknown };
};

export type GeoJSONFeatureCollection = {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
};

// ------------------------------------------------------------------- user

export type Gender = 'M' | 'F' | 'X';
export type AuthProvider = 'local' | 'google' | 'apple';

export interface User {
  id: string;
  email: string;
  displayName: string;
  authProviders: AuthProvider[];
  gender: Gender;
  /** null until the user has enough completed rounds (WHS needs 3). */
  handicapIndex: number | null;
  homeCourseId: string | null;
  /** Privacidade da timeline (canvas 1w). Simétrica: desligar corta os dois sentidos. */
  shareRoundsWithFriends: boolean;
  createdAt: string;
}

export interface Handicap {
  handicapIndex: number | null;
  /** Length of recentDifferentials, capped at 20. */
  basedOnRounds: number;
  lastUpdated: string;
}

export interface UpdateProfileInput {
  displayName?: string;
  gender?: Gender;
  homeCourseId?: string | null;
  shareRoundsWithFriends?: boolean;
}

// ------------------------------------------------------------------ auth

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
}

export interface AuthResponse {
  user: User;
  tokens: Tokens;
}

// ---------------------------------------------------------------- course

export interface TeeBox {
  name: string;
  color: string;
  courseRating: number;
  slopeRating: number;
  gender: Gender;
  totalDistance: number;
}

export interface Hole {
  number: number;
  par: number;
  /** Stroke index (SI) — 1 is the hardest hole. Named handicapIndex on the wire. */
  handicapIndex: number;
  /** Keys are a subset of the course's teeBox names. Values in metres. */
  distancesByTee: Record<string, number>;
  pin: GeoJSONPoint;
  features: GeoJSONFeatureCollection;
}

export interface CourseSummary {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
  location: GeoJSONPoint;
  totalHoles: 9 | 18;
  parTotal: number;
  verified: boolean;
  createdAt: string;
}

export interface CourseDetail extends CourseSummary {
  teeBoxes: TeeBox[];
  holes: Hole[];
  createdBy: string | null;
  updatedAt: string;
}

// ----------------------------------------------------------------- round

export type RoundStatus = 'in_progress' | 'completed' | 'abandoned';

export interface HoleScore {
  holeNumber: number;
  /** Server-derived from the course, never sent by the client. */
  par: number;
  strokes: number;
  putts: number | null;
  /** Forced to null on par 3 — a par 3 has no fairway to hit. */
  fairwayHit: boolean | null;
  /** Server-derived: strokes - putts <= par - 2. null when putts is null. */
  greenInRegulation: boolean | null;
  penaltyStrokes: number;
  bunkerShots: number;
}

export interface RoundStats {
  holesScored: number;
  totalStrokes: number;
  totalToPar: number;
  greensInRegulation: number;
  /** Denominator is every hole scored. */
  greensInRegulationPct: number;
  fairwaysHit: number;
  /** Denominator excludes par 3s — so the two percentages have different bases. */
  fairwayHitPct: number;
  totalPutts: number | null;
  puttsPerRound: number | null;
  averagePuttsPerHole: number | null;
  birdiesOrBetter: number;
  pars: number;
  bogeys: number;
  doubleOrWorse: number;
  bunkerShots: number;
  penaltyStrokes: number;
}

export interface CourseSnapshot {
  /** Frozen when the round started — a later course re-rating does not change this round. */
  courseRating: number;
  slopeRating: number;
  par: number;
}

export interface Round {
  id: string;
  userId: string;
  courseId: string;
  teeBoxName: string;
  /** Second tee when a 9-hole course is played as 18. */
  teeBoxNameBack: string | null;
  holesPlayed: 9 | 18;
  status: RoundStatus;
  startedAt: string;
  completedAt: string | null;
  scores: HoleScore[];
  totalStrokes: number | null;
  totalToPar: number | null;
  scoreDifferential: number | null;
  playerHandicapAtStart: number | null;
  /** Índice depois desta volta. Com `playerHandicapAtStart`, diz se ela baixou o handicap. */
  handicapIndexAfter: number | null;
  countsForHandicap: boolean;
  courseSnapshot: CourseSnapshot;
  stats: RoundStats;
}

/**
 * The list projection. Note what is NOT here: no course name (resolve it against the
 * course cache), no scores, no stats, and no playerHandicapAtStart — the handicap
 * evolution chart in 1l has to fetch each round individually to get that.
 */
export interface RoundSummary {
  id: string;
  courseId: string;
  teeBoxName: string;
  status: RoundStatus;
  holesPlayed: 9 | 18;
  startedAt: string;
  completedAt: string | null;
  totalStrokes: number | null;
  totalToPar: number | null;
  scoreDifferential: number | null;
  /** Presente só numa volta de flight — vira "Em grupo · N jogadores" na lista (canvas 1n). */
  groupPlayerCount: number | null;
  /** Falso no scramble: a volta existe como registro, mas não alimenta handicap nem estatística. */
  countsForHandicap: boolean;
}

export interface StartRoundInput {
  courseId: string;
  teeBoxName: string;
  teeBoxNameBack?: string;
  holesPlayed: 9 | 18;
}

export interface RecordHoleScoreInput {
  strokes: number;
  putts?: number;
  fairwayHit?: boolean;
  penaltyStrokes?: number;
  bunkerShots?: number;
}

// ------------------------------------------------------------ pagination

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------- social

/**
 * A face pública de um jogador. Note o que **não** está aqui: email. O backend tem um
 * `PublicUserDto` separado justamente porque o `User` acima é o que você vê de si mesmo.
 */
export interface PublicUser {
  id: string;
  displayName: string;
  handicapIndex: number | null;
}

export type FriendRelation = 'none' | 'friend' | 'request_sent' | 'request_received';

export interface UserSearchResult extends PublicUser {
  homeCourseName: string | null;
  mutualFriends: number;
  relation: FriendRelation;
}

export interface Friend extends PublicUser {
  friendsSince: string;
}

/**
 * O perfil de outro jogador (canvas 3a/3b).
 *
 * Estende `PublicUser` e não `User`: o canvas fecha a seção dizendo que perfil público é "nome,
 * handicap, voltas e membro desde; **email nunca**", e é o `User` que carrega o email.
 */
export interface PlayerProfile extends PublicUser {
  bestHandicapIndex: number | null;
  /** Quantos differentials sustentam o índice — o "baseado em N voltas" do cabeçalho. */
  basedOnRounds: number;
  homeCourseName: string | null;
  memberSince: string;
  friendsSince: string | null;
  completedRounds: number;
  roundsTogether: number;
}

export interface SharedRoundSide {
  roundId: string | null;
  teeBoxName: string | null;
  gross: number | null;
  /** Tacadas em relação ao par — o "+5" ao lado do score (canvas 3a). */
  toPar: number | null;
  /** Gross menos o course handicap congelado no flight. Nulo se faltar qualquer um dos dois. */
  net: number | null;
}

/** Uma volta em que vocês dois jogaram no mesmo flight. */
export interface SharedRound {
  groupRoundId: string;
  courseId: string;
  holesPlayed: 9 | 18;
  playedAt: string;
  /** O seu lado. */
  mine: SharedRoundSide;
  /** O lado de quem é dono do perfil. */
  theirs: SharedRoundSide;
}

export interface HeadToHead {
  theirs: number;
  mine: number;
  ties: number;
  /** Quantas voltas puderam ser comparadas nesta base. */
  played: number;
}

/**
 * O retrospecto vem nas duas bases porque a leitura muda de dono entre elas — no canvas, *8–1 no
 * gross vira 4–6 no net*, já que a diferença de handicap inverte a conta.
 */
export interface RoundsTogether {
  rounds: SharedRound[];
  gross: HeadToHead;
  net: HeadToHead;
}

/**
 * Quantas voltas em grupo você já jogou com alguém. Vem separado de `Friend` porque parceiro e
 * amigo não são a mesma lista — a tela cruza as duas, como já faz com o índice de campos.
 */
export interface PlayingPartner {
  userId: string;
  rounds: number;
}

export interface FriendRequest {
  id: string;
  user: PublicUser;
  direction: 'received' | 'sent';
  createdAt: string;
}

export interface FriendRequests {
  received: FriendRequest[];
  sent: FriendRequest[];
}

export interface RoundComment {
  id: string;
  roundId: string;
  author: PublicUser;
  body: string;
  createdAt: string;
}

export interface ReactionToggle {
  reacted: boolean;
  count: number;
}

/**
 * Item da timeline (canvas 1p). União discriminada por `type`, como o backend a projeta —
 * o `switch` no cartão é exaustivo e o compilador cobra o dia em que a Fase B acrescentar
 * `group_round`.
 */
export type FeedItemType = 'live_round' | 'handicap_milestone' | 'completed_round';

interface FeedItemBase {
  id: string;
  type: FeedItemType;
  actor: PublicUser;
  roundId: string;
  courseId: string;
  teeBoxName: string;
  holesPlayed: 9 | 18;
  occurredAt: string;
  reactionCount: number;
  viewerReacted: boolean;
  commentCount: number;
}

export interface LiveRoundFeedItem extends FeedItemBase {
  type: 'live_round';
  holesCompleted: number;
  /** Para o par **parcial** — só os buracos já marcados. */
  toPar: number;
  progressPct: number;
  /**
   * O convite do 2a: o flight em que você foi incluído e ainda não entrou. Vem calculado para
   * quem pediu o feed, então o mesmo cartão traz um id para o convidado e `null` para os outros.
   */
  joinableGroupRoundId: string | null;
}

export interface CompletedRoundFeedItem extends FeedItemBase {
  type: 'completed_round';
  totalStrokes: number | null;
  totalToPar: number | null;
  scoreDifferential: number | null;
  totalPutts: number | null;
  greensInRegulationPct: number;
  /** Quarta estatística, só no desktop: o cartão do celular não tem largura para ela (1u). */
  fairwayHitPct: number;
}

export interface HandicapMilestoneFeedItem extends FeedItemBase {
  type: 'handicap_milestone';
  handicapIndex: number;
  previousHandicapIndex: number;
  isPersonalBest: boolean;
}

export type FeedItem = LiveRoundFeedItem | CompletedRoundFeedItem | HandicapMilestoneFeedItem;

// ----------------------------------------------------------- volta em grupo

/**
 * Os eixos de formato do canvas 1r. `teams` (duplas, com best ball e scramble) e `match` (match
 * play) ficaram para o corte seguinte — o 1t só desenha o resultado de "cada um por si".
 */
export type GroupContest = 'individual' | 'teams';
/** Só em duplas. A diferença não é de rótulo: best ball tem quatro linhas, scramble tem duas. */
export type TeamFormat = 'best_ball' | 'scramble';
export type GroupScoring = 'stroke' | 'stableford' | 'match';
export type GroupHandicapMode = 'gross' | 'net';
export type TeamId = 'A' | 'B';

export interface GroupFormat {
  contest: GroupContest;
  teamFormat?: TeamFormat | null;
  scoring: GroupScoring;
  handicapMode: GroupHandicapMode;
}

/** "Um flight tem no máximo 4 jogadores — você é um deles." */
export const MAX_FLIGHT_PLAYERS = 4;

/** Jogador sem conta, guardado por quem o cadastrou. */
export interface GuestPlayer {
  id: string;
  displayName: string;
  handicapIndex: number | null;
  createdAt: string;
}

export type ParticipantKind = 'owner' | 'user' | 'guest';

export interface GroupParticipant {
  id: string;
  kind: ParticipantKind;
  userId: string | null;
  guestPlayerId: string | null;
  /** Congelado no início — o cartão de um dia não muda se a conta for renomeada depois. */
  displayName: string;
  handicapIndexAtStart: number | null;
  courseHandicap: number | null;
  team: TeamId | null;
  /**
   * A `Round` de quem entrou na volta. `null` no dono (a dele é o `roundId` do cartão), no
   * convidado sem conta, e em quem foi incluído mas ainda não entrou — este último é justamente
   * quem vê o convite.
   */
  roundId: string | null;
  scores: { holeNumber: number; strokes: number }[];
}

/** Um buraco de um jogador, com tudo que se sabe dele. */
export interface GroupPlayerHole {
  holeNumber: number;
  strokes: number;
  putts: number | null;
  /** `null` em par 3, onde não há fairway a acertar — nunca `false`. */
  fairwayHit: boolean | null;
  penaltyStrokes: number | null;
  bunkerShots: number | null;
}

/**
 * Onde **você** está neste flight. Vem do servidor porque é a mesma regra que a escrita aplica —
 * derivá-la no cliente seria a segunda cópia, e a que discorda em silêncio.
 */
export interface GroupViewer {
  participantId: string | null;
  isOwner: boolean;
  /** Já entrou e tem cartão próprio. */
  joined: boolean;
  /** Marca qualquer linha — o marcador do flight. */
  canScoreAll: boolean;
  /** Foi incluído, tem conta, e ainda não entrou. */
  canJoin: boolean;
}

export interface PlayerStanding {
  participantId: string;
  displayName: string;
  kind: ParticipantKind;
  handicapIndexAtStart: number | null;
  courseHandicap: number | null;
  holesScored: number;
  gross: number;
  toPar: number;
  strokesReceived: number;
  /** `null` quando o jogador não tem handicap: sem ele não há net, e a tela diz isso. */
  net: number | null;
  netToPar: number | null;
  stablefordPoints: number;
  /** Best ball: em quantos buracos este jogador foi o que contou para a dupla. */
  holesCounted: number | null;
  team: TeamId | null;
  position: number;
}

export interface TeamStanding {
  team: TeamId;
  memberIds: string[];
  /** Soma **crua** dos handicaps da dupla — a convenção do canvas no scramble. */
  combinedCourseHandicap: number | null;
  holesScored: number;
  gross: number;
  toPar: number;
  strokesReceived: number;
  net: number | null;
  netToPar: number | null;
  stablefordPoints: number;
  position: number;
}

/** `null` é **não jogado** — estado que só existe em match play e precisa ser explícito. */
export type HoleWinner = TeamId | 'halved' | null;

export interface HoleOutcome {
  holeNumber: number;
  winner: HoleWinner;
}

export interface MatchResult {
  holes: HoleOutcome[];
  /** Do ponto de vista do time A. */
  won: number;
  lost: number;
  halved: number;
  leader: TeamId | null;
  lead: number;
  remaining: number;
  /** O buraco em que a partida fechou. `null` enquanto ela está viva. */
  decidedAtHole: number | null;
}

export interface TeamHoleScore {
  holeNumber: number;
  strokes: number;
  putts?: number | null;
}

export interface GroupHole {
  holeNumber: number;
  par: number;
  /** 1 é o buraco mais difícil — é ele que distribui as tacadas de handicap. */
  strokeIndex: number;
}

export interface GroupRound {
  id: string;
  ownerId: string;
  roundId: string;
  courseId: string;
  teeBoxName: string;
  teeBoxNameBack: string | null;
  holesPlayed: 9 | 18;
  format: GroupFormat;
  participants: GroupParticipant[];
  /**
   * Os buracos de **cada** jogador, por id de participante. Era `ownerScores` enquanto só o dono
   * tinha volta; a fonte varia (própria `Round` ou o participante) e a tela não precisa saber.
   */
  scoresByParticipant: Record<string, GroupPlayerHole[]>;
  viewer: GroupViewer;
  holes: GroupHole[];
  standings: PlayerStanding[];
  teams: TeamStanding[];
  match: MatchResult | null;
  /** Só no scramble: as tacadas de cada dupla, já que não há bola individual. */
  teamScores: Record<TeamId, TeamHoleScore[]>;
  /** Falso no scramble — "nenhuma volta entra no handicap, nem a sua". */
  countsForHandicap: boolean;
  /** Vem da volta do dono: é ela que abre, fecha e é abandonada. */
  status: RoundStatus;
  startedAt: string;
  completedAt: string | null;
  scoreDifferential: number | null;
}

export interface NewParticipantInput {
  kind: 'user' | 'guest';
  userId?: string;
  guestPlayerId?: string;
  displayName?: string;
  handicapIndex?: number | null;
  team?: TeamId | null;
}

export interface CreateGroupRoundInput {
  courseId: string;
  teeBoxName: string;
  teeBoxNameBack?: string;
  holesPlayed: 9 | 18;
  format: GroupFormat;
  /** Os outros jogadores — você entra sozinho, é o seu cartão. */
  players: NewParticipantInput[];
  /** Seu time, quando a disputa é em duplas. */
  ownerTeam?: TeamId | null;
}

/** Entrar numa volta em andamento (canvas 2a). O tee é escolhido aqui, não herdado do dono. */
export interface JoinGroupRoundInput {
  teeBoxName: string;
  teeBoxNameBack?: string | null;
}

export interface RecordGroupHoleInput {
  /**
   * Uma entrada por jogador que você está marcando. Putts, fairway, penalty e bunker deixaram de
   * ser privilégio do dono quando o amigo passou a poder entrar na volta.
   */
  entries: {
    participantId: string;
    strokes: number;
    putts?: number;
    fairwayHit?: boolean;
    penaltyStrokes?: number;
    bunkerShots?: number;
  }[];
  /** Só no scramble: a tacada de cada dupla. */
  teamEntries?: { team: TeamId; strokes: number; putts?: number | null }[];
  /**
   * Sua linha, quando você é o dono. Continua existindo por compatibilidade — a linha do dono
   * também pode ir em `entries`, e é assim que o scorecard colaborativo a manda.
   */
  own?: {
    strokes: number;
    putts?: number;
    fairwayHit?: boolean;
    penaltyStrokes?: number;
    bunkerShots?: number;
  };
}
